/**
 * Patient photo store — backed by S3 through the Attachments API.
 *
 * A patient photo is an Attachment with category 'patient_photo' linked to the
 * horse; we always surface the most recently uploaded one. Images are
 * downscaled client-side (long edge -> 512px JPEG) before upload to keep S3
 * objects and avatar loads small. Reads/writes go through react-query in the
 * components, so there is no local subscription machinery here anymore.
 */
import api from './api';

export const PATIENT_PHOTO_CATEGORY = 'patient_photo';
const MAX_DIMENSION = 512;
const JPEG_QUALITY = 0.82;
const MAX_BYTES = 20 * 1024 * 1024; // 20 MB

/** React-query key for a single horse's resolved photo URL. */
export const horsePhotoQueryKey = (horseId: string | null | undefined) =>
  ['horse-photo', horseId] as const;

/** GET /attachments may return a bare array or a { data } envelope. */
function extractList(payload: any): any[] {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

/**
 * Most-recent patient-photo URL for a horse, or null if none. The endpoint
 * already orders by createdAt desc, so the first row is the latest.
 */
export async function fetchHorsePhotoUrl(horseId: string): Promise<string | null> {
  if (!horseId) return null;
  const res = await api.get('/attachments', {
    params: { horseId, category: PATIENT_PHOTO_CATEGORY },
  });
  const latest = extractList(res.data)[0];
  return latest?.storageUrl || null;
}

/**
 * Downscale + upload a patient photo to S3 (via the Attachments API) and
 * return the stored URL. Throws a user-friendly message on bad input.
 */
export async function uploadHorsePhoto(horseId: string, file: File): Promise<string> {
  if (!horseId) throw new Error('Save the patient first, then add a photo.');
  if (!file.type.startsWith('image/')) {
    throw new Error('Selected file is not an image');
  }
  // resizeImageToBlob fully decodes the source into a canvas before
  // downscaling, so reject oversized inputs up front to avoid an OOM/freeze.
  if (file.size > MAX_BYTES) {
    throw new Error('Image is too large (max 20 MB). Please choose a smaller photo.');
  }
  const blob = await resizeImageToBlob(file);
  const form = new FormData();
  form.append('file', blob, 'patient-photo.jpg');
  form.append('horseId', horseId);
  form.append('category', PATIENT_PHOTO_CATEGORY);
  const res = await api.post('/attachments/upload', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data?.storageUrl || '';
}

/** Remove all patient photos for a horse (reverts the avatar to the hoof). */
export async function removeHorsePhoto(horseId: string): Promise<void> {
  if (!horseId) return;
  const res = await api.get('/attachments', {
    params: { horseId, category: PATIENT_PHOTO_CATEGORY },
  });
  const list = extractList(res.data);
  await Promise.all(list.map((a: any) => api.delete(`/attachments/${a.id}`)));
}

// ---------------------------------------------------------------------------
// Client-side downscale
// ---------------------------------------------------------------------------

/**
 * Decode the image, draw to a canvas scaled to MAX_DIMENSION on the long edge,
 * and re-encode as a JPEG Blob ready for multipart upload.
 *
 * Uses createImageBitmap when available (faster + handles EXIF rotation) and
 * falls back to <img> + FileReader for older browsers.
 */
async function resizeImageToBlob(file: File): Promise<Blob> {
  const source = await loadImage(file);
  const srcW = (source as any).width as number;
  const srcH = (source as any).height as number;

  const longest = Math.max(srcW, srcH);
  const scale = longest > MAX_DIMENSION ? MAX_DIMENSION / longest : 1;
  const destW = Math.round(srcW * scale);
  const destH = Math.round(srcH * scale);

  const canvas = document.createElement('canvas');
  canvas.width = destW;
  canvas.height = destH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get 2D canvas context');
  ctx.drawImage(source as any, 0, 0, destW, destH);
  // Release bitmap memory if applicable.
  if ('close' in source && typeof (source as ImageBitmap).close === 'function') {
    (source as ImageBitmap).close();
  }
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob((b) => resolve(b), 'image/jpeg', JPEG_QUALITY),
  );
  if (!blob) throw new Error('Could not process the image');
  return blob;
}

async function loadImage(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file);
    } catch {
      // Fall through to the <img> path.
    }
  }
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(r.error || new Error('FileReader failed'));
    r.readAsDataURL(file);
  });
  return await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Image failed to decode'));
    img.src = dataUrl;
  });
}
